import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@bancobarigui/barihaus';
import { checklistService, Checklist, CreateChecklistRequest } from '@/services/checklists';
import { inspectionTypeService } from '@/services/inspectionType.service';
import { useAlertContext } from '@/contexts/alert.context.tsx';

export function useChecklistsIndex() {
  const navigation = useNavigate();
  const { notify } = useAlertContext();
  const [selectedInspectionType, setSelectedInspectionType] = useState<string | undefined>(undefined);
  const [showOnlyActive, setShowOnlyActiveInternal] = useState(false);
  const [inspectionTypes, setInspectionTypes] = useState<{ value: string; label: string }[]>([]);
  const [loadingInspectionTypes, setLoadingInspectionTypes] = useState(false);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const setShowOnlyActive = (value: boolean) => {
    setShowOnlyActiveInternal(value);
  };

  useEffect(() => {
    const loadInspectionTypes = async () => {
      setLoadingInspectionTypes(true);
      try {
        const types = await inspectionTypeService.getAllInspectionTypes();
        const options = types
          .filter((type) => type.isActive)
          .map((type) => ({
            value: type.id,
            label: type.name,
          }));
        setInspectionTypes(options);
      } catch {
        // Ignore errors loading inspection types
      } finally {
        setLoadingInspectionTypes(false);
      }
    };

    loadInspectionTypes();
  }, []);

  const fetchChecklists = async (inspectionTypeId?: string, page: number = 1, size: number = 10) => {
    setLoading(true);
    setError(null);

    try {
      const result = inspectionTypeId
        ? await checklistService.getChecklistByInspectionType(inspectionTypeId, page, size)
        : await checklistService.getAllChecklists(page, size);

      setChecklists(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar checklists');
      setChecklists([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklists(selectedInspectionType, currentPage, pageSize);
  }, [selectedInspectionType, currentPage, pageSize]);

  const handleNew = () => {
    navigation('/checklist/new');
  };

  const handleEdit = (id: string) => {
    navigation(`/checklist/edit/${id}`);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Confirmar exclusão',
      content: 'Tem certeza que deseja remover este checklist? Esta ação não pode ser desfeita.',
      okText: 'Remover',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      icon: null,
      onOk: async () => {
        try {
          await checklistService.deleteChecklist(id);
          await fetchChecklists(selectedInspectionType, currentPage, pageSize);
          notify({
            message: 'Checklist deletado com sucesso!',
            type: 'success',
          });
        } catch (err: any) {
          notify({
            message: 'Não foi possível remover o checklist.',
            type: 'error'
          });
        }
      },
    });
  };

  const handleCopy = async (id: string) => {
    try {
      const original = await checklistService.getChecklistById(id);

      const copyData = {
        checklistName: `${original.name} (Cópia)`,
        inspectionTypeId: original.inspectionTypeId,
        groups: original.groups.map((group) => ({
          name: group.name,
          sections: group.sections.map((section) => ({
            name: section.name,
            questions: section.questions.map((question) => ({
              title: question.title,
              answerType: question.answerType,
              isRequired: question.isRequired,
              showInSimplifiedReport: question.showInSimplifiedReport,
              hasHint: question.hasHint,
              hintText: question.hintText,
              hasDisplayRule: question.hasDisplayRule,
              displayRule: question.displayRule,
              options: question.options,
              maxCharacters: question.maxCharacters,
              decimalPlaces: question.decimalPlaces,
              tableColumns: question.tableColumns,
              description: question.description,
              maxPhotos: question.maxPhotos,
              allowGallerySelection: question.allowGallerySelection,
              maxFiles: question.maxFiles,
              allowedFileTypes: question.allowedFileTypes,
              answerKey: question.answerKey,
              tags: question.tags,
            })),
          })),
        })),
      } as CreateChecklistRequest;

      await checklistService.createChecklist(copyData);
      fetchChecklists(selectedInspectionType, currentPage, pageSize);
    } catch {
      // Ignore errors when copying checklist
    }
  };

  const handleActivate = (id: string) => {
    Modal.confirm({
      title: 'Confirmar ativação',
      content: 'Tem certeza que deseja ativar este checklist?',
      okText: 'Ativar',
      cancelText: 'Cancelar',
      icon: null,
      onOk: async () => {
        try {
          await checklistService.activateChecklist(id);
          await fetchChecklists(selectedInspectionType, currentPage, pageSize);
          notify({
            message: 'Checklist ativado com sucesso!',
            type: 'success',
          });
        } catch {
          notify({
            message: 'Não foi possível ativar o checklist.',
            type: 'error'
          });
        }
      },
    });
  };

  const handleDeactivate = (id: string) => {
    Modal.confirm({
      title: 'Confirmar desativação',
      content: 'Tem certeza que deseja desativar este checklist?',
      okText: 'Desativar',
      cancelText: 'Cancelar',
      icon: null,
      onOk: async () => {
        try {
          await checklistService.deactivateChecklist(id);
          await fetchChecklists(selectedInspectionType, currentPage, pageSize);
          notify({
            message: 'Checklist desativado com sucesso!',
            type: 'success',
          });
        } catch {
          notify({
            message: 'Não foi possível desativar o checklist.',
            type: 'error'
          });
        }
      },
    });
  };

  const formatName = (name: string): string => {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map((word) => {
        if (word.length === 0) return word;
        // Mantém preposições/artigos minúsculos (exceto a primeira palavra)
        const lowerWords = ['de', 'da', 'do', 'das', 'dos', 'e', 'a', 'o'];
        if (lowerWords.includes(word)) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ')
      // Garante que a primeira letra fique maiúscula
      .replace(/^./, (c) => c.toUpperCase());
  };

  const columns = useMemo(() => [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => formatName(name),
    },
    {
      title: 'Tipo de Inspeção',
      dataIndex: 'inspectionTypeName',
      key: 'inspectionTypeName',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean) => (isActive ? 'Ativo' : 'Inativo'),
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('pt-BR'),
    },
    {
      title: 'Término',
      dataIndex: 'deactivatedAt',
      key: 'deactivatedAt',
      width: 180,
      render: (date: string | undefined) =>
        date ? new Date(date).toLocaleString('pt-BR') : '-',
    },
  ], []);

  const filteredChecklists = useMemo(() => {
    if (showOnlyActive) {
      return checklists.filter((checklist) => !checklist.deactivatedAt);
    }
    return checklists;
  }, [checklists, showOnlyActive]);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  return {
    inspectionTypes,
    loadingInspectionTypes,
    selectedInspectionType,
    setSelectedInspectionType,
    showOnlyActive,
    setShowOnlyActive,
    columns,
    checklists: filteredChecklists,
    totalRecords: totalCount,
    currentPage,
    pageSize,
    loading,
    error,
    handleNew,
    handleEdit,
    handleDelete,
    handleCopy,
    handleActivate,
    handleDeactivate,
    handlePageChange,
  };
}
